// Module 08 contracts: UAE identity and document verification (ADR-017, verification stream).
// Messages are i18n keys (PD-015). Sensitive identifiers never appear in responses unmasked except in
// the audited reveal response for an identity reviewer.
import { z } from 'zod';

const Uuid = z.uuid();
const trimmed = (max: number) => z.string().trim().min(1, 'v.required').max(max, 'v.tooLong');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'v.tooLong')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'v.date');
const Key = z.string().trim().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'v.policyKey');
const Version = z.number().int().min(1);
const Label = z.object({ en: trimmed(80), ar: z.string().trim().max(80, 'v.tooLong').optional() });
const Token = z.string().regex(/^[A-Za-z0-9_-]{20,200}$/, 'v.invalid');

export const IDENTITY_METHOD_VALUES = ['uae_pass', 'emirates_id_reader', 'passport_mrz', 'document_ocr', 'manual'] as const;
export const CAPTURE_METHOD_VALUES = ['emirates_id_reader', 'passport_mrz', 'document_ocr'] as const;
export const ATTRIBUTE_VALUES = ['given_name', 'family_name', 'full_name_ar', 'document_number', 'document_type', 'issuing_country', 'nationality', 'date_of_birth', 'sex', 'expiry_date', 'provider_subject', 'assurance_level', 'mobile', 'email'] as const;
export const VALIDATOR_VALUES = ['ae-eid@1', 'icao-mrz@1', 'passport-number@1', 'sa-nid@1', 'kw-civil@1', 'bh-cpr@1', 'om-civil@1', 'qa-qid@1', 'licence-generic@1'] as const;
export const PROOF_VALUES = ['identity', 'nationality', 'work_authorisation'] as const;
export const SIMULATOR_SCENARIOS = ['valid', 'unreadable', 'expired', 'bad_check_digit', 'timeout'] as const;

// ------------------------------------------------------------------ settings (PD-191)
export const VerificationSettingsSchema = z.object({
  schema: z.literal(1),
  methods: z.object({ uae_pass: z.boolean(), emirates_id_reader: z.boolean(), passport_mrz: z.boolean(), document_ocr: z.boolean(), manual: z.literal(true) }),
  documentTypes: z
    .array(
      z.object({
        code: Key,
        label: Label,
        kind: z.enum(['national_id', 'passport', 'driving_licence', 'residence_permit']),
        jurisdiction: z.string().regex(/^([A-Z]{2}|\*)$/, 'v.invalid'),
        validator: z.enum(VALIDATOR_VALUES),
        proves: z.array(z.enum(PROOF_VALUES)).min(1).max(3),
        limitations: z.array(Key).max(10),
        enabled: z.boolean(),
        sites: z.array(Uuid).max(200).nullable(),
        autoVerify: z.boolean(),
      }),
    )
    .min(1)
    .max(40),
  extraction: z.object({ purpose: Label, allowed: z.array(z.enum(ATTRIBUTE_VALUES)).max(ATTRIBUTE_VALUES.length) }),
  images: z.object({ store: z.boolean(), purpose: Label.nullable(), retentionClass: Key, retentionDays: z.number().int().min(1).max(3650).nullable() }),
  review: z.object({ secondReviewerForCorrections: z.boolean(), secondReviewerForManual: z.boolean(), allowAcceptUnscannedDocuments: z.boolean() }),
  uaePass: z.object({ autoAccept: z.boolean(), minimumAssurance: z.enum(['SOP1', 'SOP2', 'SOP3']), signing: z.object({ enabled: z.boolean() }) }),
  retention: z.object({ evidenceClass: Key, evidenceDays: z.number().int().min(1).max(3650).nullable() }),
});
export type VerificationSettingsInput = z.infer<typeof VerificationSettingsSchema>;

export const UpdateVerificationSettingsRequest = z.object({
  expectedVersion: z.number().int().min(0),
  settings: VerificationSettingsSchema,
  /** PB-08-10: required when turning image storage off while images are held. */
  heldImagesDecision: z.enum(['retain_until_retention', 'request_disposal']).optional(),
});

export interface VerificationSettingsResponse {
  readonly settings: VerificationSettingsInput;
  readonly version: number;
  readonly heldImages: number;
  readonly entitlements: { readonly uaePass: boolean; readonly uaePassSigning: boolean };
  readonly simulators: boolean;
  readonly adapters: ReadonlyArray<{ readonly key: string; readonly method: string; readonly model: string; readonly firmware: string; readonly validated: boolean; readonly simulator: boolean }>;
}

// ------------------------------------------------------------------ evidence (PB-08-04 … PB-08-11)
export const CaptureRequest = z.object({
  participationId: Uuid,
  method: z.enum(CAPTURE_METHOD_VALUES),
  adapter: Key,
  documentTypeCode: Key.optional(),
  input: z.object({
    scenario: z.enum(SIMULATOR_SCENARIOS).optional(),
    mrz: z.string().max(200, 'v.tooLong').optional(),
    fields: z.partialRecord(z.enum(ATTRIBUTE_VALUES), z.string().trim().max(100, 'v.tooLong')).optional(),
    confidence: z.number().min(0).max(1).optional(),
  }),
  replaceCurrent: z.boolean().default(false),
});

export const ManualVerificationRequest = z.object({
  participationId: Uuid,
  /** null = a document type the organisation has not configured (manual policy path, PB-08-06). */
  documentTypeCode: Key.nullable(),
  observed: z.object({
    givenName: trimmed(100),
    familyName: optionalText(100),
    documentNumber: optionalText(40),
    expiryDate: LocalDate.nullable().optional(),
    jurisdiction: z.string().regex(/^[A-Z]{2}$/, 'v.invalid').nullable().optional(),
  }),
  outcome: z.enum(['verified', 'rejected', 'unavailable']),
  reason: optionalText(1000),
  sourceDocumentId: Uuid.optional(),
  replaceCurrent: z.boolean().default(false),
});

export const DecideEvidenceRequest = z.object({ expectedVersion: Version, decision: z.enum(['accept', 'reject', 'unavailable']), reason: optionalText(1000) });
export const CorrectEvidenceRequest = z.object({
  expectedVersion: Version,
  fields: z.object({ givenName: trimmed(100).optional(), familyName: trimmed(100).optional(), documentNumber: trimmed(40).optional(), expiryDate: LocalDate.optional() }),
  reason: trimmed(1000),
});
export const RevealRequest = z.object({ reason: trimmed(300) });
export const MatchDecisionRequest = z.object({ candidatePersonId: Uuid, decision: z.enum(['link', 'separate']), reason: optionalText(1000) });
// PB-05-07 (PD-450): validUntil sets or corrects the document's validity date with the decision; omitted = keep the stated date.
export const DocumentDecisionRequest = z.object({ expectedVersion: Version, decision: z.enum(['accept', 'reject']), method: z.enum(['content_reviewed', 'original_sighted']), reason: optionalText(1000), validUntil: LocalDate.nullable().optional() });
/** PB-05-07 (PD-453): a reviewer corrects the validity date of a current document; the status and earlier history stay. */
export const DocumentValidityRequest = z.object({ expectedVersion: Version, validUntil: LocalDate.nullable(), reason: trimmed(1000) });
export const VerificationQueueQuery = z.object({ siteId: Uuid.optional() });

export interface IdentityEvidenceDto {
  readonly id: string;
  readonly participationId: string;
  readonly method: string;
  readonly documentType: string | null;
  readonly documentTypeLabel: string | null;
  readonly jurisdiction: string | null;
  readonly validator: string | null;
  readonly status: string;
  readonly statusReason: string | null;
  readonly checks: Record<string, unknown>;
  readonly attributes: Record<string, string>;
  readonly discardedFields: readonly string[];
  readonly documentNumberMasked: string | null;
  readonly documentExpiresOn: string | null;
  readonly proves: readonly string[];
  readonly limitations: readonly string[];
  readonly provenance: Record<string, unknown>;
  readonly correctedFields: readonly string[];
  readonly requiresSecondReviewer: boolean;
  readonly capturedBy: string | null;
  readonly capturedVia: string;
  readonly decidedBy: string | null;
  readonly decidedAt: string | null;
  readonly decisionReason: string | null;
  readonly superseded: boolean;
  readonly createdAt: string;
  readonly version: number;
  readonly history: ReadonlyArray<{ readonly from: string | null; readonly to: string; readonly reason: string | null; readonly at: string; readonly actor: string | null }>;
  readonly images: ReadonlyArray<{ readonly id: string; readonly contentType: string; readonly disposalRequested: boolean }>;
  readonly can: { readonly decide: boolean; readonly correct: boolean; readonly reveal: boolean; readonly match: boolean };
}

export interface VisitorDocumentDto {
  readonly id: string;
  readonly requirementKey: string;
  readonly status: string;
  readonly statusReason: string | null;
  readonly contentType: string;
  readonly byteSize: number;
  readonly uploadedAt: string;
  readonly reviewMethod: string | null;
  readonly reviewReason: string | null;
  readonly reviewedAt: string | null;
  readonly version: number;
  readonly objectUrl: string;
  /**
   * PD-707 / PD-648 (clamav stream): malware check of the upload. `openable` = the file may be
   * opened (clean, or no scanner configured); `rescan` = an explicit, audited retry is possible.
   */
  readonly malwareCheck?: { readonly status: 'not_scanned' | 'clean' | 'infected' | 'failed'; readonly checkedAt: string | null; readonly openable: boolean; readonly rescan: boolean };
  /** PB-05-07: recorded validity date and who stated it last (visitor on upload, or a reviewer). */
  readonly validUntil?: string | null;
  readonly validUntilSource?: 'visitor' | 'reviewer' | null;
  /** The policy asks for a validity date for this document (PD-450). */
  readonly validUntilRequired?: boolean;
}

export interface ParticipationIdentityResponse {
  readonly participationId: string;
  readonly siteId: string;
  readonly visitor: { readonly displayName: string };
  readonly requirement: { readonly level: 'none' | 'warn' | 'required'; readonly acceptedMethods: readonly string[]; readonly requiredProofs: readonly string[] };
  readonly fact: { readonly status: string; readonly satisfied: boolean; readonly shortfall: string | null };
  readonly current: IdentityEvidenceDto | null;
  readonly previous: readonly IdentityEvidenceDto[];
  readonly documents: readonly VisitorDocumentDto[];
  readonly transactions: ReadonlyArray<{ readonly id: string; readonly kind: string; readonly environment: string; readonly status: string; readonly statusReason: string | null; readonly createdAt: string }>;
  readonly options: { readonly methods: readonly string[]; readonly documentTypes: ReadonlyArray<{ readonly code: string; readonly label: string; readonly kind: string; readonly jurisdiction: string }>; readonly adapters: VerificationSettingsResponse['adapters'] };
  readonly can: { readonly capture: boolean; readonly verify: boolean };
}

export interface VerificationQueueItem {
  readonly kind: 'evidence' | 'document';
  readonly id: string;
  readonly participationId: string;
  readonly visitId: string;
  readonly siteId: string;
  readonly visitorName: string;
  readonly status: string;
  readonly label: string;
  readonly createdAt: string;
}

export interface MatchCandidateDto {
  readonly personId: string;
  readonly maskedName: string;
  readonly strength: 'document' | 'name_and_jurisdiction' | 'name_other_jurisdiction';
  readonly matchedFields: readonly string[];
  readonly lastVisitAt: string | null;
  readonly decision: 'linked' | 'kept_separate' | null;
}

// ------------------------------------------------------------------ UAE PASS (PB-08-01 … PB-08-03, PB-08-13)
export const ProviderConfigRequest = z.object({
  environment: z.enum(['staging', 'production']),
  adapter: Key,
  issuer: z.url('v.url').max(300, 'v.tooLong'),
  clientId: trimmed(200),
  /** Write-only; omit to keep the stored secret. */
  clientSecret: z.string().min(8, 'v.tooShort').max(500, 'v.tooLong').optional(),
  scopes: z.array(z.string().trim().min(1).max(200)).min(1).max(10),
  expectedVersion: z.number().int().min(0),
});
export const OnboardingRecordRequest = z.object({
  item: z.enum(['service_provider_approval', 'agreement', 'staging_assessment', 'go_live_approval']),
  outcome: z.enum(['approved', 'passed', 'signed', 'rejected', 'failed']),
  reference: trimmed(300),
  note: optionalText(1000),
});
export const ActivateProviderRequest = z.object({ environment: z.enum(['staging', 'production']), expectedVersion: Version });
export const DisableProviderRequest = z.object({ environment: z.enum(['staging', 'production']), expectedVersion: Version, reason: trimmed(300) });

export interface ProviderEnvironmentDto {
  readonly environment: 'staging' | 'production';
  readonly adapter: string;
  readonly issuer: string;
  readonly clientId: string;
  readonly hasSecret: boolean;
  readonly scopes: readonly string[];
  readonly status: string;
  readonly activatedAt: string | null;
  readonly version: number;
}
export interface UaePassOverviewResponse {
  readonly entitled: boolean;
  readonly signingEntitled: boolean;
  readonly environments: readonly ProviderEnvironmentDto[];
  readonly onboarding: ReadonlyArray<{ readonly id: string; readonly item: string; readonly outcome: string; readonly reference: string; readonly note: string | null; readonly recordedBy: string; readonly recordedAt: string }>;
  readonly productionGate: { readonly ok: boolean; readonly missing: readonly string[]; readonly selfApproved: boolean };
  readonly approvedScopes: { readonly authentication: readonly string[]; readonly signing: readonly string[] };
}

export const PublicIdentityStatusRequest = z.object({ token: Token });
export const StartUaePassRequest = z.object({ token: Token, consent: z.literal(true, 'ver.v.consentRequired') });
export const StartSigningRequest = z.object({ token: Token, consent: z.literal(true, 'ver.v.consentRequired'), acknowledgementId: Uuid });
export const UaePassCallbackRequest = z.object({
  state: z.string().regex(/^[A-Za-z0-9_-]{20,200}$/, 'v.invalid'),
  code: z.string().max(8000, 'v.tooLong').optional(),
  error: z.string().max(100, 'v.tooLong').optional(),
});

export interface PublicIdentityStatus {
  readonly requirement: 'none' | 'warn' | 'required';
  readonly status: string;
  readonly satisfied: boolean;
  readonly options: { readonly uaePass: boolean; readonly assisted: boolean };
  readonly purpose: string;
  readonly pending: boolean;
  /** PD-727: demo mode — UAE PASS would be offered but is not available in the demo (no provider, no simulator). */
  readonly demoUnavailable?: true;
}
export interface StartUaePassResponse {
  readonly authorizationUrl: string;
  readonly expiresAt: string;
}
export interface UaePassCallbackResponse {
  readonly outcome: 'verified' | 'review_pending' | 'cancelled' | 'failed' | 'unknown' | 'signed';
}
